/**
 * Pure attestation-state rules for a clinical note — `docs/roadmap.md` Phase 3, C6.
 *
 * Isolated from the database and the HTTP layer so `noteAttestation.test.ts` can cover every rule
 * with no Postgres, per `claude-kit/rules/invariants.md`'s "Business rules live in pure, tested
 * modules — never inline in a component". The service layer (`server/src/clinical/notesService.ts`)
 * calls these; it does not re-derive them.
 *
 * **The credential-aware co-sign gate, stated once here.** C6 calls for co-signing to be restricted
 * to "a licensed supervising provider." Two independent checks now compose to enforce that
 * (`BACKLOG.md` C6b, landed 2026-08-20): `authorization.ts`'s `note:cosign` capability (`physician`
 * alone — not `advanced_practice_provider`, whose own row is commented "does not co-sign others"),
 * enforced at the route layer; **and** `credentials.ts`'s `hasActiveCosignCredential`, checked by
 * `notesService.ts`'s `cosignNote` against the caller's real `user_credentials` rows — an active,
 * unexpired MD or DO credential, not just the role label. A role held with no matching credential on
 * file no longer passes. This module does not re-implement either check; it only expresses the
 * note-*state* rules (draft vs. signed, author vs. reviewer) that apply once both gates have already
 * passed.
 */

export const NOTE_STATUSES = ['draft', 'signed'] as const;
export type NoteStatus = (typeof NOTE_STATUSES)[number];

export function isNoteStatus(value: string): value is NoteStatus {
  return (NOTE_STATUSES as readonly string[]).includes(value);
}

/**
 * The minimal shape these rules need — never the full row, so a caller cannot hand this module a
 * note's clinical `content` and have it accidentally become part of a decision.
 */
export interface NoteAttestationSubject {
  readonly status: string;
  readonly authorId: string;
  readonly signedBy: string | null;
}

/**
 * May `callerId` edit this note's content through the plain update path?
 *
 * Only the author, and only while it is still a draft. A note, once signed, is immutable via this
 * path by design: `signedAt`/`signedBy` describe a specific version of the content, and an edit
 * after signing would silently invalidate an attestation that already happened. Documenting more
 * after signing is an addendum — a new note — not an edit to this one.
 */
export function canEditNote(note: NoteAttestationSubject, callerId: string): boolean {
  return note.status === 'draft' && note.authorId === callerId;
}

/**
 * May `callerId` sign this note?
 *
 * Signing is self-attestation and is never delegable: only the author may sign their own note, and
 * only while it is a draft. Holding write access to the same patient's chart is not enough — signing
 * is not an edit-permission grant to anyone else, it is a specific person swearing to specific
 * content they themselves wrote.
 */
export function canSignNote(note: NoteAttestationSubject, callerId: string): boolean {
  return note.status === 'draft' && note.authorId === callerId;
}

/**
 * May this note be co-signed at all, independent of who is asking?
 *
 * The note must already be signed. A co-sign on an unsigned note has nothing to supervise yet, and
 * `signedBy` would still be null — there would be no answer to "who is this co-signature attesting
 * over".
 */
export function canCosignNote(note: NoteAttestationSubject): boolean {
  return note.status === 'signed';
}

/**
 * Is `callerId` a distinct reviewer from whoever signed the note?
 *
 * Co-signing is supervisory attestation over *someone else's* note. A clinician's own signature is
 * already their attestation; letting them "co-sign" their own note would make the field meaningless
 * as a second-reviewer signal in a chart review or an audit, and would let a solo prescriber invent
 * supervision that never happened.
 */
export function canCosignAsDistinctReviewer(
  note: NoteAttestationSubject,
  callerId: string,
): boolean {
  return note.signedBy !== callerId;
}
